import { useEffect, useId, useState } from 'react';

const BASE_Z_INDEX = 10000;
let currentZIndex = BASE_Z_INDEX;
const activePortals = new Map<string, number>();

export const portalManager = {
  /**
   * Acquiert le prochain z-index disponible pour un portail
   * @param id - Identifiant unique du portail
   * @returns Le z-index attribué
   */
  acquire(id: string): number {
    // Si le portail a déjà un z-index, le retourner
    const existing = activePortals.get(id);
    if (existing !== undefined) {
      return existing;
    }

    currentZIndex += 1;
    activePortals.set(id, currentZIndex);
    return currentZIndex;
  },

  /**
   * Libère le z-index d'un portail quand il se ferme
   * @param id - Identifiant unique du portail
   */
  release(id: string): void {
    activePortals.delete(id);
  },

  /**
   * Obtient le z-index actuel d'un portail
   * @param id - Identifiant unique du portail
   * @returns Le z-index ou undefined si non trouvé
   */
  get(id: string): number | undefined {
    return activePortals.get(id);
  },

  /**
   * Retourne le z-index de base
   */
  getBaseZIndex(): number {
    return BASE_Z_INDEX;
  },

  /**
   * Retourne le nombre de portails actifs
   */
  getActiveCount(): number {
    return activePortals.size;
  },
};
/**
 * Hook pour gérer automatiquement le z-index d'un portail
 * Le z-index est acquis à l'ouverture et libéré à la fermeture
 *
 * @param isOpen - État d'ouverture du portail
 * @returns Le z-index à utiliser, ou null si fermé
 *
 * @example
 * const zIndex = usePortalZIndex(isOpen);
 * return isOpen ? createPortal(<div style={{ zIndex }}>...</div>, document.body) : null;
 */
export const usePortalZIndex = (isOpen: boolean): number | null => {
  const id = useId();
  const [zIndex, setZIndex] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      const newZIndex = portalManager.acquire(id);
      setZIndex(newZIndex);
    } else {
      portalManager.release(id);
      setZIndex(null);
    }

    return () => {
      portalManager.release(id);
    };
  }, [isOpen, id]);

  return zIndex;
};
